/**
 * The one list of products the console offers.
 *
 * The platform owner manages the list in Settings → Products; the auth
 * service keeps it and every signed-in user reads it. Every place that shows
 * products (the home grid, /products, the sidebar, the header menu, search)
 * draws from this module, so what the owner set appears in all of them and
 * nothing else does. Built-in products ship with the platform (their pages are
 * part of the console); the owner can hide, rename or re-describe them. Custom
 * products are the owner's own links.
 */
import { dashboardUrl } from '@/lib/dashboardLink'
import { isProductVisible, ProductId } from '@/config/siteConfig'

/** A product as the auth service stores it. */
export interface ProductRecord {
  id: string
  name: string
  description: string
  icon: string
  href: string
  external: boolean
  enabled: boolean
  sort_order: number
  builtin: boolean
}

export interface ProductDef {
  id: string
  name: string
  description: string
  /** Locale key of a built-in product's texts, when it still has its shipped description. */
  key?: string
  iconPath: string
  href: (language: string | undefined) => string
  external: boolean
  enabled: boolean
  builtin: boolean
}

const BUILTIN_KEYS: Record<string, string> = {
  'synstream-enterprise': 'synstreamEnterprise',
  'jshine-camera-health': 'jshineCameraHealth',
  'pipeline-studio': 'pipelineStudio',
  'auto-monitor': 'autoMonitor'
}

/** The shipped products, as the service seeds them; shown until the list has been read. */
export const BUILTIN_DEFAULTS: ProductRecord[] = [
  { id: 'synstream-enterprise', name: 'Synstream Enterprise', description: 'Connect your systems and turn data into action', icon: '/icon.png', href: 'dashboard', external: true, enabled: true, sort_order: 10, builtin: true },
  { id: 'jshine-camera-health', name: 'JShine Camera Health', description: 'Camera-health monitoring for the J.SHINE fleet', icon: '/icon.png', href: 'https://dashboard-jshine.uat.synstream.co/', external: true, enabled: true, sort_order: 15, builtin: true },
  { id: 'pipeline-studio', name: 'Pipeline Studio', description: 'Data pipeline management', icon: '/PS.png', href: '/products/pipeline-studio', external: false, enabled: true, sort_order: 20, builtin: true },
  { id: 'auto-monitor', name: 'Auto Monitor', description: 'Application monitoring', icon: '/AM.png', href: 'https://apac.eginnovations.com/', external: true, enabled: true, sort_order: 30, builtin: true }
]

export function toDef(r: ProductRecord): ProductDef {
  const shipped = BUILTIN_DEFAULTS.find((b) => b.id === r.id)
  const keepsShippedText = !!shipped && r.description === shipped.description
  return {
    id: r.id,
    name: r.name,
    description: r.description,
    key: r.builtin && keepsShippedText ? BUILTIN_KEYS[r.id] : undefined,
    iconPath: r.icon,
    // The built-in dashboard's address is the site's own dashboard host
    href: r.href === 'dashboard' ? (language) => dashboardUrl(language) : () => r.href,
    external: r.external,
    enabled: r.enabled,
    builtin: r.builtin
  }
}

/** A product's description in the reader's language: the locale text for an unchanged built-in, else what the owner typed. */
export function describe(p: ProductDef, translated: string | undefined): string {
  return p.key && translated ? translated : p.description
}

// ---------------------------------------------------------------------------
// The store. One list for the whole console, read from the service once after
// sign-in and again whenever the owner changes it; components subscribe through
// useVisibleProducts() so a change reaches every menu at once.
// ---------------------------------------------------------------------------

let all: ProductDef[] | undefined // undefined = not read yet
let manage = false // the service says this account may change the list (an installation superuser)
let loading: Promise<void> | null = null
let pending = false // the first read is in flight: show nothing rather than a list that may be wrong
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((l) => l())
}

/** Replace the list with what the service holds and tell subscribers. */
export function setProducts(records: ProductRecord[], canManage?: boolean) {
  if (canManage !== undefined) manage = canManage
  all = [...records].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)).map(toDef)
  pending = false
  notify()
}

/**
 * Read the list from the service. One request at a time, shared by every
 * component that asked; a failed read is retried a few times, since the first
 * can run before the session is complete (the sign-in terms, for one). `force`
 * re-reads an already loaded list, which the hook does whenever the signed-in
 * user changes.
 */
export function loadProducts(fetcher: () => Promise<{ products: ProductRecord[]; can_manage: boolean }>, force = false): Promise<void> {
  if (loading) return loading
  if (all !== undefined && !force) return Promise.resolve()
  if (all === undefined) { pending = true; notify() }
  const attempt = (left: number): Promise<void> =>
    fetcher()
      .then((r) => setProducts(r.products, r.can_manage))
      .catch(() => {
        if (left <= 0) throw new Error('product list unavailable')
        return new Promise<void>((res) => setTimeout(res, 1500)).then(() => attempt(left - 1))
      })
  loading = attempt(3)
    .catch(() => {
      // The service never answered: fall back to the shipped list, filtered by the site default
      setProducts(BUILTIN_DEFAULTS.map((b) => ({ ...b, enabled: isProductVisible(b.id as ProductId) })), false)
    })
    .finally(() => { loading = null; pending = false; notify() })
  return loading
}

/** Forget the list, for sign-out. */
export function resetProducts() {
  all = undefined
  manage = false
  loading = null
  pending = false
  notify()
}

export function subscribeProducts(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

let version = 0
subscribeProducts(() => { version++ })
export function productsVersion(): number {
  return version
}

/** Whether this account may change the list: the service's answer, never the role. */
export function canManageProducts(): boolean {
  return manage
}

/** Every product the service holds, hidden ones included (for the settings page). */
export function allProducts(): ProductDef[] {
  if (all === undefined) return BUILTIN_DEFAULTS.map(toDef)
  return all
}

/** The products this site shows, in menu order. */
export function visibleProductList(): ProductDef[] {
  if (pending) return []
  if (all === undefined) return BUILTIN_DEFAULTS.filter((b) => isProductVisible(b.id as ProductId)).map(toDef)
  return all.filter((p) => p.enabled)
}

export function productById(id: string): ProductDef | undefined {
  return allProducts().find((p) => p.id === id)
}

/** True when the product is shown; same answer as visibleProductList. */
export function productShown(id: string): boolean {
  return visibleProductList().some((p) => p.id === id)
}
